<template>
  <div>
    <vue-element-loading :active="loading" />

    <b-table
      id="retail-order-table-list"
      class="align-middle"
      responsive
      outlined
      bordered
      stacked="sm"
      :busy.sync="isBusy"
      :items="tableProvider"
      :fields="fields"
      :current-page="currentPage"
      :per-page="perPage"
    >
      <template v-slot:cell(history)="data">
        <VStatusBadge
          v-if="data.item.history.events.length > 0"
          :events="data.item.history.events"
        />
        <VStatusBadge
          v-else
          :static_status="default_order_status"
        />
      </template>

      <template v-slot:cell(payment)="data">
        <div v-if="data.item.payment">
          <div
            v-for="(payment_transaction, index) in data.item.payment.transactions"
            :key="index"
          >{{ translatePaymentMethod(payment_transaction.payment_method.payment_method) }}</div>
        </div>
      </template>

      <template v-slot:cell(payment_status)="data">
        <b-badge
          v-if="data.item.payment.payed"
          class="p-2"
          variant="success"
        >{{ $t("retail_order_list.payment_status_payed") }}</b-badge>
        <b-badge
          v-else-if="data.item.payment.referenced"
          class="p-2"
          variant="primary"
        >{{ $t("retail_order_list.payment_status_referenced") }}</b-badge>
        <b-badge
          v-else
          class="p-2"
          variant="warning"
        >
          {{ $t("retail_order_list.payment_status_pending") }}
        </b-badge>
      </template>

      <template v-slot:cell(documentation)="data">
        <div v-if="data.item.invoice_request">
          <div v-if="getInvoiceDocs(data.item.documentation.documents)">
            <div
              v-for="(doc, index) in getInvoiceDocs(data.item.documentation.documents)"
              :key="index"
              class="pull-left"
            >
              <a v-if="doc.file_type==5" :href="doc.file">| PDF</a>
              <a v-if="doc.file_type==6" :href="doc.file">| XML</a>
            </div>
          </div>
          <div v-else>
            <b-badge
              class="p-2"
              variant="success"
            >{{ $t("retail_order_list.invoice_requested") }}</b-badge>
          </div>
        </div>
        <div v-else>
          <b-button
            size="sm"
            variant="warning"
            :id="'invoice_popover' + data.item.uuid"
            @click="openInvoicePopOver('invoice_popover' + data.item.uuid)"
          >
            {{ $t("retail_order_list.request_invoice") }}
          </b-button>
          <b-popover
            :ref="'invoice_popover' + data.item.uuid"
            :target="'invoice_popover' + data.item.uuid"
          >
            <template v-slot:title>
              <b-button
                @click="closeInvoicePopOver('invoice_popover' + data.item.uuid)"
                class="close"
                variant="close"
                aria-label="Close"
              >
                <span class="d-inline-block" aria-hidden="true">&times;</span>
              </b-button>
              {{ $t("retail_order_list.request_invoice") }}
            </template>
            <div v-if="!data.item.payment.payed">
              <h5 class="mb-2">
                {{ $t("retail_order_list.form_invoice_not_payed_title") }}
              </h5>
            </div>
            <div v-else>
              <h3 class="mb-2">
                {{ $t("retail_order_list.form_invoice_title") }}
              </h3>
              <p>
                {{ $t("retail_order_list.form_invoice_subtext") }}
              </p>
              <b-form-group
                :label="$t('retail_order_list.form_invoice_name')"
                label-for="invoice_name"
                label-cols="12"
                :state="form_state.name"
                class="mb-1"
                :invalid-feedback="form_invalid_feedback.name"
              >
                <b-form-input
                  ref="invoice_name"
                  id="invoice_name"
                  v-model="form.name"
                  :state="form_state.name"
                ></b-form-input>
              </b-form-group>
              {{ form_invalid_feedback.invoice_rfc }}
              <b-form-group
                :label="$t('retail_order_list.form_invoice_rfc')"
                label-for="invoice_rfc"
                label-cols="12"
                :state="form_state.rfc"
                class="mb-1"
                :invalid-feedback="form_invalid_feedback.rfc"
              >
                <b-form-input
                  ref="invoice_rfc"
                  id="invoice_rfc"
                  v-model="form.rfc"
                  :state="form_state.rfc"
                ></b-form-input>
              </b-form-group>
              <div class="m-5 d-flex justify-content-between">
                <b-button
                  type="submit"
                  variant="primary"
                  @click="onInvoiceFormSubmit(data.item)"
                >
                  {{ $t("retail_order_list.form_button_ok") }}
                </b-button>
              </div>
            </div>
          </b-popover>
        </div>
      </template>

      <template v-slot:cell(actions)="data" v-if="show_action_column">
        <div class="d-flex">
          <b-button
            v-if="show_order_detail"
            size="xs"
            variant="primary-b"
            @click="routeToOrderList"
          >{{ $t("retail_order_list.hide_details_refund_button") }}</b-button>
          <b-button
            v-else
            size="xs"
            variant="primary-b"
            @click="routeToDetails(data.item)"
          >{{ $t("retail_order_list.details_refund_button") }}</b-button>
          <b-link
            v-if="!data.item.importation"
            href="#"
            class="repeat-order ml-auto"
            @click="repeatOrder(data.item)"
          >
            <font-awesome-icon icon="redo" class="mr-1"></font-awesome-icon>
            {{ $t("retail_order_list.repeat_order") }}
          </b-link>
        </div>
      </template>
    </b-table>
    <b-row v-if="show_pagination">
      <b-col md="12">
        <Pagination
          v-show="totalRows > perPage"
          :total="totalRows"
          :perPage="perPage"
          v-model="currentPage"
          class="float-md-right"
        />
      </b-col>
    </b-row>
  </div>
</template>


<script>
import get from "lodash/get";
import VStatusBadge from "@/components/VStatusBadge";
import Pagination from "@/components/Pagination";
// Esto tiene sentido porque los índices de los payment aquí están ordenados de
// modo que coincidan con el ID de payment method, 0, 1, 2, 3...
const payment_method_translator = [
  "retail_order_list.payment_method_none",
  "retail_order_list.payment_method_ewallet_paypal",
  "retail_order_list.payment_method_ewallet_openpay",
  "retail_order_list.payment_method_debit_credit_card",
  "retail_order_list.payment_method_bank_transfer",
  "retail_order_list.payment_method_direct_debit",
  "retail_order_list.payment_method_voucher",
  "retail_order_list.payment_method_cash",
  "retail_order_list.payment_method_cash_paynet",
  "retail_order_list.payment_method_webpay",
];

const HISTORY_EVENT_PENDING = 4;

export default {
  name: "retail-order-table-list",
  components: {
    VStatusBadge,
    Pagination,
  },
  props: {
    pt_uuid: String,
    i_uuid: String,
    static_items: {
      type: Array,
      default: undefined,
    },
    show_action_column: {
      type: Boolean,
      default: true,
    },
    show_pagination: {
      type: Boolean,
      default: true,
    },
    show_order_detail: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      form: {
        invoice_name: "",
        invoice_rfc: "",
      },
      form_state: {
        invoice_name: undefined,
        invoice_rfc: undefined,
      },
      form_invalid_feedback: {
        invoice_name: undefined,
        invoice_rfc: undefined,
      },
      search_term: "",
      datepicker_options: {
        format: "YYYY-MM-DD",
      },
      isBusy: false,
      currentPage: 1,
      perPage: 20,
      totalRows: 0,
      loading: true,
      default_order_status: HISTORY_EVENT_PENDING,
    };
  },
  computed: {
    fields() {
      return [
        {
          key: "human_code",
          label: this.$t("retail_order_list.headtable_id"),
          class: "selectable text-center",
          thClass: "unselectable text-center",
          formatter: (value, key, item) => {
            if (value) {
              return value;
            }
            if (item.mdk_order_id) {
              return item.mdk_order_id;
            }
            return undefined;
          },
        },
        {
          key: "created_date",
          label: this.$t("retail_order_list.headtable_date"),
          class: "selectable text-center",
          thClass: "unselectable text-center",
          formatter: value => {
            return this.i18nFormatDate(value);
          },
        },
        {
          key: "amount_total",
          formatter: (value, key, item) => {
            if (!get(item, "amount.total")) {
              return "---";
            }
            return this.$formatMoney(
              item.amount.total,
              item.amount.total_currency
            );
          },
          label: this.$t("retail_order_list.headtable_total"),
          class: "selectable text-center",
          thClass: "unselectable text-center",
        },

        {
          key: "payment",
          label: this.$t("retail_order_list.headtable_payment_method"),
          class: "selectable text-center",
          thClass: "unselectable text-center",
          formatter: (value, key, item) => {
            return JSON.parse(JSON.stringify(item));
          },
        },

        {
          key: "history",
          label: this.$t("retail_order_list.headtable_status"),
          class: "selectable text-center",
          thClass: "unselectable text-center",
        },

        {
          key: "payment_status",
          label: this.$t("retail_order_list.headtable_payment_status"),
          class: "selectable text-center",
          thClass: "unselectable text-center",
        },

        {
          key: "documentation",
          label: this.$t("retail_order_list.headtable_invoice"),
          class: "selectable text-center",
          thClass: "unselectable text-center",
        },

        {
          key: "actions",
          label: "",
          thClass: this.show_action_column ? "" : "d-none",
          tdClass: this.show_action_column ? "" : "d-none",
        },
      ];
    },
  },
  fallbackI18n: {
    messages: {
      und: {
        retail_order_list: {
          headtable_date: "Date",
          headtable_id: "ID",
          headtable_status: "Status",
          headtable_total: "Total",
          headtable_payment_method: "Payment method",
          headtable_payment_status: "Payment status",
          headtable_invoice: "Invoice",
          details_refund_button: "Details",
          hide_details_refund_button: "Hide details",
          payment_method_none: "None",
          payment_method_ewallet_paypal: "Ewallet paypal",
          payment_method_ewallet_openpay: "Ewallet openpay",
          payment_method_debit_credit_card: "Debit credit card",
          payment_method_bank_transfer: "Bank transfer",
          payment_method_direct_debit: "Direct debit",
          payment_method_voucher: "Voucher",
          payment_method_cash: "Cash",
          payment_method_cash_paynet: "Cash paynet",
          payment_method_webpay: "WebPay Plus",
          payment_status_pending: "Pending",
          payment_status_referenced: "Referenced",
          payment_status_payed: "Payed",
          repeat_order: "Place the order again",
          request_invoice: "Invoice",
          form_invoice_name: "Name",
          form_invoice_rfc: "RFC",
          form_invoice_title: "Request your invoice",
          form_invoice_not_payed_title:
            "The invoice cannot be requested until the payment is done",
          form_button_cancel: "Cancel",
          form_button_ok: "Request",
          form_invoice_subtext: "Please fill the form and click on Request.",
          invoice_requested: "Invoice requested",
          error: {
            cannot_load_refunds: "Cannot load refunds",
            cannot_repeat_order: "Cannot repeat order",
            cannot_request_invoice: "Cannot request invoice",
          },
        },
      },
    },
  },
  methods: {
    getInvoiceDocs(documents) {
      const result = documents.filter(
        doc => doc.file_type == 5 || doc.file_type == 6
      );
      if (result[0]) {
        return result;
      }
      return undefined;
    },
    repeatOrder(order) {
      this.loading = true;
      this.$store.dispatch("shoppingcart/REPEAT_ORDER", order).then(
        () => {
          this.loading = false;
          this.$router.push({ name: "cart_review" });
        },
        err => {
          this.$sentry({ capture: err });
          this.mnotify_error({
            text: this.$t("retail_order_list.error.cannot_repeat_order"),
          });
          this.loading = false;
          return [];
        }
      );
    },
    routeToDetails(order) {
      this.$router.replace({
        name: "order-details",
        params: {
          order_uuid: order.uuid,
        },
      });
    },
    routeToOrderList() {
      this.$router.replace({
        name: "userhistorial",
      });
    },
    translatePaymentMethod(method) {
      if (!payment_method_translator[method]) {
        return "Unknown";
      }
      return this.$t(payment_method_translator[method]);
    },
    tableProvider() {
      if (this.static_items) {
        this.isBusy = false;
        this.loading = false;
        var copy = [];
        this.static_items.forEach(element => {
          copy.push(JSON.parse(JSON.stringify(element)));
        });
        return copy;
      }
      try {
        return this.$store
          .dispatch("drugtransaction/GET_RETAIL_ORDER_LIST", {
            per_page: this.perPage,
            current_page: this.currentPage,
            search: this.search_term,
          })
          .then(
            response => {
              const items = response.data.results;
              this.isBusy = false;
              this.loading = false;
              this.totalRows = response.data.count;
              return items;
            },
            err => {
              this.$sentry({ capture: err });
              this.mnotify_error({
                text: this.$t("retail_order_list.error.cannot_load_refunds"),
              });
              this.isBusy = false;
              this.loading = false;
              return [];
            }
          );
      } catch (e) {
        this.$sentry({ capture: e });
        this.mnotify_error({
          text: this.$t("retail_order_list.error.cannot_load_refunds"),
        });
        this.isBusy = false;
        this.loading = false;
        return [];
      }
    },
    openInvoicePopOver(popover_uuid) {
      this.$refs[popover_uuid].$emit("open");
    },
    closeInvoicePopOver(popover_uuid) {
      this.$refs[popover_uuid].$emit("close");
    },
    onInvoiceFormSubmit(item) {
      this.$store
        .dispatch("drugtransaction/RETAILORDER_REQUEST_INVOICE", {
          order_uuid: item.uuid,
          data: this.form,
        })
        .then(
          res => {
            item.invoice_request = res.data.uuid;
          },
          err => {
            this.showResponseError(err);
            this.putFormFeedback(err.response);
          }
        );
    },
  },
};
</script>

<style lang="scss" scoped>
#refund-table-list ::v-deep thead th:last-child {
  width: 10%;
}

#retail-order-table-list ::v-deep th,
#retail-order-table-list ::v-deep td {
  vertical-align: middle;
}

@include media-breakpoint-down(sm) {
  ::v-deep table.b-table > tbody > tr > td {
    grid-template-columns: 30% auto !important;

    &::before {
      text-align: left !important;
    }
  }
}

.repeat-order {
  color: $corp-color1;
  text-decoration: none;
}
</style>
